import { useCallback, useMemo } from 'react';
import { playSound, Sound } from '~/assets/sounds';
import { Text } from '~/components/ui/Text';
import {
	StyledArrow,
	StyledButtonContainer,
	StyledContainer,
	StyledName,
	StyledNameContainer,
	StyledRightContainer,
	StyledVariant,
} from './SelectItem.styled';

export interface ISelectValue {
	value: any;
	title: string;
}

interface ISelectItemProps {
	items: ISelectValue[];
	value: ISelectValue;
	name?: string;
	onChange: (value: ISelectValue) => void;
	count?: boolean;
	fullWidth?: boolean;
	onlyCount?: boolean;
	loop?: boolean;
}

const SelectItem: React.FC<ISelectItemProps> = ({
	items,
	value,
	name,
	onChange,
	count,
	fullWidth,
	onlyCount,
	loop,
}: ISelectItemProps) => {
	const activeIndex = useMemo(
		() => items.map((item) => item.value).indexOf(value.value),
		[items, value],
	);

	const handlePrev = useCallback(() => {
		const newIndex =
			activeIndex > 0 ? activeIndex - 1 : loop ? items.length - 1 : 0;
		onChange(items[newIndex]);
	}, [activeIndex, items, onChange, loop]);

	const handleNext = useCallback(() => {
		const newIndex =
			activeIndex < items.length - 1 ? activeIndex + 1 : loop ? 0 : activeIndex;
		onChange(items[newIndex]);
	}, [items, activeIndex, onChange, loop]);

	return (
		<StyledContainer $fullWidth={fullWidth} $onlyCount={onlyCount}>
			{onlyCount ? (
				<Text>
					{activeIndex + 1}{' '}
					<span style={{ opacity: 0.5 }}>/ {items.length}</span>
				</Text>
			) : (
				<StyledNameContainer>
					<StyledName>{name || value.title}</StyledName>
					{count && (
						<StyledVariant>
							({activeIndex + 1} / {items.length})
						</StyledVariant>
					)}
				</StyledNameContainer>
			)}
			<StyledRightContainer>
				<StyledButtonContainer
					onClick={() => {
						handlePrev();
						playSound(Sound.ClickButton);
					}}
					$disabled={!loop && activeIndex === 0}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<StyledArrow />
				</StyledButtonContainer>
				<StyledButtonContainer
					onClick={() => {
						handleNext();
						playSound(Sound.ClickButton);
					}}
					$disabled={!loop && activeIndex === items.length - 1}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<StyledArrow style={{ transform: 'rotate(180deg)' }} />
				</StyledButtonContainer>
			</StyledRightContainer>
		</StyledContainer>
	);
};

export { SelectItem };
